/** Sessions-as-children under a beneficiary (Campaigns/Sessions v2 UX).
 *
 * Lazy-loads a beneficiary's (membership's) sessions and renders each with
 * its made date, status, and the actions the operator needs:
 *   • Open now / Open  → the session page (join / manage)
 *   • Reschedule       → the session page (pending sessions only)
 *   • Send reminder    → re-fires the join link to a still-pending invite
 *
 * "Open now" is shown prominently when the session is live (open/recording)
 * or due (scheduled within a short window).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExternalAudioUpload } from "@/modules/sessions";
import { Link } from "react-router-dom";

import { ApiPaths, api } from "@/lib/api";
import type { SessionStatus } from "@/lib/types";

/** One FIELD invite's full delivery timeline, raw from the backend — see
 *  `_serialize_invite_for_beneficiary` (apps/campaigns/views.py). */
type BeneficiaryInvite = {
  id: string;
  created_at: string | null;
  recipient_email: string;
  recipient_phone: string;
  email_sent_at: string | null;
  sms_sent_at: string | null;
  /** One entry per reminder actually sent, per channel.
   *
   *  Was eight fixed `reminder_*_sent_at` keys. The schedule is the
   *  operator's choice now, so there is no fixed set — and an entry can
   *  say a reminder was ABANDONED, which a stamp could not: "gave up"
   *  used to be written as the same value as "sent". */
  reminders: {
    reminder_id: string;
    channel: "email" | "sms";
    offset_minutes: number;
    outcome: "sent" | "gave_up";
    attempts: number;
    sent_at: string | null;
  }[];
  redeemed_at: string | null;
  /** The same working join link the recipient got, reconstructed from
   *  the persisted delivery token — null once redeemed/cancelled (the
   *  link is dead, nothing to copy). See `_serialize_invite_for_
   *  beneficiary` (apps/campaigns/views.py). */
  join_url: string | null;
};

type BeneficiarySession = {
  id: string;
  title: string;
  status: SessionStatus;
  scheduled_for: string | null;
  started_at: string | null;
  ended_at: string | null;
  created_at: string | null;
  has_pending_invite: boolean;
  /** Present only when the caller opted in (single-beneficiary
   *  drill-down) — absent on the bulk operator-beneficiaries listing. */
  invites?: BeneficiaryInvite[];
};

/** "24h", "15min" — the largest unit that divides exactly, so the
 *  day-before reminder does not read as "1440min". */
function formatOffset(
  minutes: number,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  if (minutes % 1440 === 0)
    return `${minutes / 1440}${t("notify.abbr.days")}`;
  if (minutes % 60 === 0) return `${minutes / 60}${t("notify.abbr.hours")}`;
  return `${minutes}${t("notify.abbr.minutes")}`;
}

/** Flattens one invite's events into a chronological, labelled list
 *  list — "here's exactly when the system sent something" (2026-09-16
 *  backlog). Skips any stamp that's still null (never sent / n/a). */
function inviteEvents(
  inv: BeneficiaryInvite,
  t: ReturnType<typeof useTranslation>["t"],
): { key: string; label: string; at: string; channel: "email" | "sms" | "neutral" }[] {
  const rows: Array<[string, string | null, string, "email" | "sms" | "neutral"]> = [
    ["issued", inv.created_at, t("leads.invites.issued", "Invite created"), "neutral"],
    ["email", inv.email_sent_at, t("leads.invites.emailSent", "Email sent"), "email"],
    ["sms", inv.sms_sent_at, t("leads.invites.smsSent", "SMS sent"), "sms"],
    // Every reminder that actually went out, in whatever schedule this
    // org configured — so a 15-minute reminder somebody added shows up
    // here, which eight hardcoded rows could never have done.
    ...inv.reminders.map(
      (r) =>
        [
          `rem-${r.reminder_id}-${r.channel}`,
          r.sent_at,
          r.outcome === "gave_up"
            ? t("leads.invites.reminderGaveUp", {
                tier: formatOffset(r.offset_minutes, t),
                count: r.attempts,
              })
            : t(
                r.channel === "sms"
                  ? "leads.invites.reminderSms"
                  : "leads.invites.reminderEmail",
                { tier: formatOffset(r.offset_minutes, t) },
              ),
          r.channel,
        ] as [string, string | null, string, "email" | "sms" | "neutral"],
    ),
    [
      "opened",
      inv.redeemed_at,
      t("leads.invites.opened", "Link opened by the beneficiary"),
      "neutral",
    ],
  ];
  return rows
    .filter((r): r is [string, string, string, "email" | "sms" | "neutral"] => r[1] !== null)
    .map(([key, at, label, channel]) => ({ key, label, at, channel }))
    .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
}

const DUE_WINDOW_MS = 15 * 60 * 1000; // "open now" once within 15 min of start

function isLive(s: BeneficiarySession): boolean {
  if (s.status === "open" || s.status === "recording") return true;
  if (s.ended_at || s.status === "closed" || s.status === "expired") {
    return false;
  }
  if (!s.scheduled_for) return false;
  return new Date(s.scheduled_for).getTime() - Date.now() <= DUE_WINDOW_MS;
}

function isPending(s: BeneficiarySession): boolean {
  return !s.ended_at && s.status !== "closed" && s.status !== "expired";
}

export function BeneficiarySessions({
  campaignId,
  membershipId,
  onReschedule,
}: {
  campaignId: string;
  membershipId: string;
  /** Opens the caller's schedule modal. Without it the per-session
   *  "Reschedule" falls back to navigating to the session, which is
   *  what it did before — and what made it useless. */
  onReschedule?: () => void;
}) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["membership-sessions", campaignId, membershipId],
    queryFn: async () =>
      (
        await api.get<{ sessions: BeneficiarySession[] }>(
          ApiPaths.campaignMembershipSessions(campaignId, membershipId),
        )
      ).data.sessions,
    retry: false,
  });

  if (q.isLoading) {
    return <p className="py-2 text-xs text-nb-text-3">{t("common.loading")}</p>;
  }
  if (!q.data || q.data.length === 0) {
    return (
      <p className="py-2 text-xs text-nb-text-3">
        {t("campaigns.detail.noSessionsForBeneficiary")}
      </p>
    );
  }

  return (
    <ul className="space-y-1.5 py-1">
      {q.data.map((s) => (
        <SessionChild key={s.id} session={s} onReschedule={onReschedule} />
      ))}
    </ul>
  );
}

function SessionChild({
  session: s,
  onReschedule,
}: {
  session: BeneficiarySession;
  onReschedule?: () => void;
}) {
  const { t } = useTranslation();
  const [reminded, setReminded] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const [resent, setResent] = useState(false);
  const live = isLive(s);
  const pending = isPending(s);
  const when = s.scheduled_for || s.created_at;

  // The still-live invite, if any — the one whose link is actually
  // worth copying/resending. `join_url` is null on a redeemed/
  // cancelled/pre-delivery-token invite (see _serialize_invite_for_
  // beneficiary), so filtering on it already excludes those.
  const activeInvite = [...(s.invites ?? [])]
    .reverse()
    .find((inv) => inv.join_url);

  const remind = useMutation({
    mutationFn: async () => {
      await api.post(ApiPaths.sessionRemind(s.id), {});
    },
    onSuccess: () => setReminded(true),
  });

  const resendInvite = useMutation({
    mutationFn: async () => {
      if (!activeInvite) return;
      await api.post(ApiPaths.sessionInviteResend(s.id, activeInvite.id));
    },
    onSuccess: () => setResent(true),
  });

  // Same clipboard-with-fallback pattern as SessionJoinLinkCard
  // (2026-09-08 fix: navigator.clipboard silently rejects outside a
  // secure/focused context — fall back to execCommand via a hidden
  // input, and say so if BOTH paths fail instead of doing nothing).
  const copyInputRef = useRef<HTMLInputElement | null>(null);

  // The copy button flashes "copied"/"failed" then reverts on a timer.
  // Backlog 2026-09-22 — that timer used to be a bare `setTimeout` with
  // no cleanup, so a component unmounted inside the window (navigating
  // away right after copying — or, in CI, the test env tearing down)
  // fired `setCopyState` against a dead tree. In the suite that surfaced
  // as `ReferenceError: window is not defined` thrown from React's
  // scheduler AFTER every test had already passed, failing the job on a
  // timing race. Track the handle so it can be cancelled on unmount and
  // superseded by a later copy.
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    },
    [],
  );
  const scheduleCopyReset = (ms: number) => {
    if (copyResetTimer.current) clearTimeout(copyResetTimer.current);
    copyResetTimer.current = setTimeout(() => {
      copyResetTimer.current = null;
      setCopyState("idle");
    }, ms);
  };

  const copyLink = () => {
    if (!activeInvite?.join_url) return;
    const url = activeInvite.join_url;
    const onOk = () => {
      setCopyState("copied");
      scheduleCopyReset(1500);
    };
    const onFail = () => {
      const input = copyInputRef.current;
      let legacyOk = false;
      if (input) {
        input.select();
        try {
          legacyOk = document.execCommand("copy");
        } catch {
          legacyOk = false;
        }
      }
      if (legacyOk) {
        onOk();
      } else {
        setCopyState("failed");
        scheduleCopyReset(4000);
      }
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(onOk).catch(onFail);
    } else {
      onFail();
    }
  };

  return (
    <li
      className="rounded-md border border-nb-border/60 bg-nb-surface/20 px-2.5 py-1.5 text-sm"
      data-testid={`beneficiary-session-${s.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-nb-mint">
          {s.title || s.id.slice(0, 8)}
        </span>
        {when && (
          <span className="text-xs text-nb-text-3">
            {formatDateTime(when)}
          </span>
        )}
        <span
          className={
            "pill text-[10px] " +
            (live ? "bg-emerald-500/20 text-emerald-300" : "")
          }
        >
          {s.status}
        </span>

        {/* Wraps: the audio uploader expands into a drop zone that takes
            a line of its own rather than being crushed into the row. */}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
          {live ? (
            <Link to={`/sessions/${s.id}`} className="btn-primary text-xs">
              {t("campaigns.detail.openNow")}
            </Link>
          ) : (
            <Link to={`/sessions/${s.id}`} className="btn-ghost text-xs">
              {t("campaigns.detail.openSession")}
            </Link>
          )}
          {/* She asked to upload the phone-interview audio "to the
              client's profile". It is offered here, on the profile, but
              attaches to THIS inspection — a beneficiary has many over
              time, and a recording belonging to none of them has no
              place in any report. */}
          <ExternalAudioUpload sessionId={s.id} />
          {/* Backlog 2026-09-22 — answer the inspection questions after
              the fact. Always available (including once the session is
              closed): the Q&A API and panel have never gated on status,
              it just wasn't reachable from here. */}
          <Link
            to={`/sessions/${s.id}/media?tab=qa`}
            className="btn-ghost text-xs"
            title={t("campaigns.detail.fillQaHint", "Answer the inspection questions")}
          >
            {t("campaigns.detail.fillQa", "Q&A")}
          </Link>
          {/* This was a <Link to={/sessions/:id}> — a button labelled
              "Reschedule" that rescheduled nothing and dropped the
              operator into the call room to work it out for herself.
              Now it opens the same modal the drawer's own button does,
              which is membership-scoped exactly like the backend
              endpoint it posts to. */}
          {pending && !live && (
            onReschedule ? (
              <button
                type="button"
                className="btn-ghost text-xs"
                title={t("campaigns.detail.rescheduleHint")}
                onClick={onReschedule}
                data-testid="session-row-reschedule"
              >
                {t("campaigns.detail.reschedule")}
              </button>
            ) : (
              <Link
                to={`/sessions/${s.id}`}
                className="btn-ghost text-xs"
                title={t("campaigns.detail.rescheduleHint")}
              >
                {t("campaigns.detail.reschedule")}
              </Link>
            )
          )}
          {pending && !live && activeInvite && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={copyLink}
              title={t("participantsPanel.copyLinkTitle")}
            >
              {copyState === "copied"
                ? t("participantsPanel.linkCopied")
                : copyState === "failed"
                  ? t("participantsPanel.copyFailed")
                  : t("participantsPanel.copyLink")}
            </button>
          )}
          {pending && !live && activeInvite && (
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={resendInvite.isPending || resent}
              onClick={() => resendInvite.mutate()}
              title={t("participantsPanel.resendTitle")}
            >
              {resent
                ? t("campaigns.detail.inviteResent", "Invite resent")
                : resendInvite.isPending
                  ? t("participantsPanel.resending")
                  : t("participantsPanel.resend")}
            </button>
          )}
          {pending && s.has_pending_invite && (
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={remind.isPending || reminded}
              onClick={() => remind.mutate()}
            >
              {reminded
                ? t("campaigns.detail.reminderSent")
                : remind.isPending
                  ? t("common.saving")
                  : t("campaigns.detail.sendReminder")}
            </button>
          )}
        </div>
      </div>
      {/* Hidden input backing the execCommand("copy") fallback — same
          shape as SessionJoinLinkCard's fix for clipboard API silently
          rejecting outside a secure/focused context. */}
      {activeInvite?.join_url && (
        <input
          ref={(el) => {
            copyInputRef.current = el;
          }}
          readOnly
          value={activeInvite.join_url}
          className="sr-only"
          aria-hidden="true"
          tabIndex={-1}
        />
      )}
      {remind.isError && (
        <span className="mt-1 block text-xs text-cvp-danger">
          {t("campaigns.detail.reminderError")}
        </span>
      )}
      {resendInvite.isError && (
        <span className="mt-1 block text-xs text-cvp-danger">
          {t("campaigns.detail.resendInviteError", "Could not resend the invite")}
        </span>
      )}
      <InviteTimeline invites={s.invites} />
    </li>
  );
}

/** Collapsible "who got what, when" for a session's FIELD invite(s) —
 *  every email/SMS/reminder stamp, chronologically, so the operator can
 *  confirm exactly when a wave send / manual reminder actually went out.
 *  Renders nothing when the caller didn't opt in (`invites` is undefined,
 *  e.g. the bulk operator-beneficiaries listing) or there's nothing sent
 *  yet. */
function InviteTimeline({ invites }: { invites?: BeneficiaryInvite[] }) {
  const { t } = useTranslation();
  if (!invites) return null;
  const events = invites.flatMap((inv) =>
    inviteEvents(inv, t).map((e) => ({
      ...e,
      // Show the channel the event actually went out on — an SMS-sent
      // row showing the recipient's email (rather than the phone number
      // it was actually sent to) was misleading.
      recipient:
        e.channel === "sms"
          ? inv.recipient_phone || inv.recipient_email
          : inv.recipient_email || inv.recipient_phone,
    })),
  );
  if (events.length === 0) return null;

  return (
    <details className="group mt-1.5">
      <summary className="cursor-pointer text-xs text-nb-text-3 hover:text-nb-text-2">
        <span className="inline-flex items-center gap-1">
          {t("leads.invites.heading", "Communications")}
          <span className="pill text-[10px]">{events.length}</span>
          <svg
            className="h-2.5 w-2.5 transition-transform group-open:rotate-180"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M3 4.5l3 3 3-3" />
          </svg>
        </span>
      </summary>
      <ul className="mt-1 space-y-0.5 border-l border-nb-border/60 pl-2.5 text-xs text-nb-text-2">
        {events.map((e, i) => (
          <li key={`${e.key}-${i}`} className="flex flex-wrap items-baseline gap-x-1.5">
            <span>{e.label}</span>
            {e.recipient && (
              <span className="text-nb-text-3">· {e.recipient}</span>
            )}
            <span className="text-nb-text-3">— {formatDateTime(e.at)}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
